import { Tag } from '@nimbus-ds/components';
import { MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { TagIcon } from '@tiendanube/icons';
import PriceTablesMenu from 'App/components/Menu/components/MainMenu/components/ProductsMenu/components/PriceTablesMenu';
import ShowByRole from 'App/components/ShowByRole';
import {
  B2B_WHOLESALE,
  CATALOG_ST_BET_B,
  NEW_ADMIN_SUBSCRIPTIONS_SECTION,
  NEW_ADMIN_SUBSCRIPTIONS_ENABLED,
} from 'App/featuresFlags';
import { useHandleMenuClick } from 'App/hooks';
import objectToUrlParams from 'commons/utils/objectToUrlParams';
import { useGetTags, useHasCatalogInventory } from 'domains/Auth/hooks';
import useHasMultipleLocations from 'domains/Catalog/hooks/useHasMultipleLocations/useHasMultipleLocations';
import { useInventory } from 'domains/Catalog/Inventory/pages/hooks';
import { useProductsList } from 'domains/Catalog/Products/pages/hooks';
import {
  trackingProductsMenuClick,
  trackingProductsMenuListClick,
} from 'domains/Catalog/Products/tracking';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations/useLocations';

const PATH = {
  products: '/products',
  inventory: '/inventory',
  transfers: '/transfers-products',
  categories: '/categories',
  subscriptions: '/subscriptions',
  priceTables: '/price-tables',
};

interface ProductsMenuProps {
  isMobile: boolean;
}

function ProductsMenu({ isMobile }: Readonly<ProductsMenuProps>) {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const { handleClickItem, handleClickAccordion } = useHandleMenuClick();
  const { appliedFilters: appliedProductFilters } = useProductsList();
  const { filters: appliedInventoryFilters } = useInventory();
  const hasCatalogInventory = useHasCatalogInventory();
  const tags = useGetTags();
  const locationsData = useLocations();
  const hasMultipleLocations = useHasMultipleLocations(locationsData.locations);
  const hasCatalogStBetB = tags.includes(CATALOG_ST_BET_B);

  const hasAccountWholesaleTag = tags.includes(B2B_WHOLESALE);
  const hasAccessToSubscriptions =
    tags.includes(NEW_ADMIN_SUBSCRIPTIONS_SECTION) &&
    tags.includes(NEW_ADMIN_SUBSCRIPTIONS_ENABLED);

  const activeProducts = Object.values(PATH).some((each) =>
    pathname.startsWith(each),
  );

  const handleProductsMenuClick = () => {
    trackingProductsMenuClick();
    handleClickAccordion();
  };

  const handleProductsMenuListClick = () => {
    trackingProductsMenuListClick();
    handleClickItem();
  };

  const menuProducts = [
    {
      children: `${t('menu.manage.products.productsList')}`,
      onClick: handleProductsMenuListClick,
      active: pathname.startsWith(PATH.products),
      href: `${PATH.products}?${objectToUrlParams(appliedProductFilters)}`,
    },
    ...(hasCatalogInventory
      ? [
          {
            children: `${t('menu.manage.products.inventory')}`,
            onClick: handleClickItem,
            active: pathname.startsWith(PATH.inventory),
            label: {
              id: 'inventory',
              label: `${t('menu.new')}`,
              appearance: 'primary',
            },
            href: `${PATH.inventory}?${objectToUrlParams(
              appliedInventoryFilters,
            )}`,
          },
        ]
      : []),
    ...(hasCatalogStBetB && hasMultipleLocations
      ? [
          {
            children: `${t('menu.manage.products.transfers')}`,
            onClick: handleClickItem,
            active: pathname.startsWith(PATH.transfers),
            href: PATH.transfers,
          },
        ]
      : []),
    {
      children: `${t('menu.manage.products.categories')}`,
      onClick: handleClickItem,
      active: pathname.startsWith(PATH.categories),
      label: isMobile && {
        id: 'categories',
        label: `${t('menu.new')}`,
        appearance: 'primary',
      },
      href: PATH.categories,
    },
    ...(hasAccessToSubscriptions
      ? [
          {
            children: `${t('menu.manage.products.subscriptions')}`,
            onClick: handleClickItem,
            active: pathname.startsWith(PATH.subscriptions),
            tag: true,
            href: PATH.subscriptions,
          },
        ]
      : []),
    ...(hasAccountWholesaleTag
      ? [
          {
            id: 'priceTables',
            children: `${t('menu.manage.products.priceTables.text')}`,
            onClick: handleClickItem,
            active: pathname.startsWith(PATH.priceTables),
            href: PATH.priceTables,
            tag: {
              label: `${t('menu.manage.products.priceTables.tagText')}`,
            },
            productUpdates: {
              title: `${t('menu.manage.products.priceTables.tooltipTitle')}`,
              description: `${t(
                'menu.manage.products.priceTables.tooltipText',
              )}`,
            },
          },
        ]
      : []),
  ];

  const itemsProducts = [...menuProducts];

  return (
    <ShowByRole includeRoles="products">
      <MenuButton.Accordion
        contentid="content-products"
        menuButton={{
          id: 'control-products',
          label: t('menu.manage.products.title'),
          startIcon: TagIcon,
          'aria-controls': 'content-products',
          onClick: handleProductsMenuClick,
        }}
        active={activeProducts}
        open={activeProducts}
        as={Link}
        to={PATH.products}
      >
        {itemsProducts.map((item, index) => {
          if (item.id === 'priceTables') {
            return <PriceTablesMenu key={`${item.id}-${index}`} item={item} />;
          }

          return (
            <MenuButton
              key={index}
              as={Link}
              label={item.children}
              onClick={item.onClick}
              to={item.href ?? ''}
              active={item.active}
            >
              {item.tag && (
                <Tag appearance="primary">
                  {typeof item.tag === 'object' && item.tag.label
                    ? item.tag.label
                    : t('menu.new')}
                </Tag>
              )}
            </MenuButton>
          );
        })}
      </MenuButton.Accordion>
    </ShowByRole>
  );
}

export default ProductsMenu;
